import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getOrders } from '../../services/orderService'
import { apiError } from '../../../admin/api/apiError'
import { useOrderActions, type OrderPatch } from '../../hooks/useOrderActions'
import type { DealerDeliveryFilter, DealerOrderItem } from '../../types'
import { formatDateTime, formatTime, formatVehicle, isCopiedNotSent, lastDaysRange, toIsoDay } from '../../utils/format'
import {
  CopiedHint, DeliveryBadge, IssuePills, ReportLinkActions, SendAction,
} from '../../components/Orders/OrderParts'
import OrderDetailPanel from '../../components/Orders/OrderDetailPanel'
import PhoneEditor from '../../components/Orders/PhoneEditor'
import { ChevronLeftIcon, ChevronRightIcon, SearchIcon } from '../../components/Orders/icons'
import './RequestsPage.css'

const PAGE_SIZE = 20
const SEARCH_DEBOUNCE_MS = 350

// вкладка «Обработка» = заказы без готового отчёта (delivery=NOT_READY)
const TABS: { value: DealerDeliveryFilter; key: string }[] = [
  { value: 'ALL', key: 'tab_all' },
  { value: 'NOT_SENT', key: 'tab_not_sent' },
  { value: 'SENT', key: 'tab_sent' },
  { value: 'VIEWED', key: 'tab_viewed' },
  { value: 'NOT_READY', key: 'tab_processing' },
]
const DELIVERY_VALUES = TABS.map((tab) => tab.value)

type Period = 'all' | 'today' | '7d' | '30d' | 'custom'
const PERIODS: Period[] = ['all', 'today', '7d', '30d', 'custom']

// диапазон дат для выбранного периода; «all» — без ограничения
function periodRange(period: Period, customFrom: string, customTo: string): { from?: string; to?: string } {
  switch (period) {
    case 'today': {
      const today = toIsoDay(new Date())
      return { from: today, to: today }
    }
    case '7d':
      return lastDaysRange(7)
    case '30d':
      return lastDaysRange(30)
    case 'custom':
      return { from: customFrom || undefined, to: customTo || undefined }
    default:
      return {}
  }
}

export default function RequestsPage() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const [searchParams, setSearchParams] = useSearchParams()

  // состояние фильтров живёт в URL: работают ссылки вида /requests?delivery=NOT_SENT и «Назад»
  const deliveryParam = searchParams.get('delivery') as DealerDeliveryFilter | null
  const delivery: DealerDeliveryFilter = deliveryParam && DELIVERY_VALUES.includes(deliveryParam) ? deliveryParam : 'ALL'
  const q = searchParams.get('q') ?? ''
  const periodParam = searchParams.get('period') as Period | null
  const period: Period = periodParam && PERIODS.includes(periodParam) ? periodParam : 'all'
  const customFrom = searchParams.get('from') ?? ''
  const customTo = searchParams.get('to') ?? ''
  const page = Math.max(0, Number(searchParams.get('page')) || 0)
  const { from, to } = periodRange(period, customFrom, customTo)

  const [items, setItems] = useState<DealerOrderItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  // ключ i18n ошибки загрузки списка; null — ошибки нет
  const [error, setError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [notSentCount, setNotSentCount] = useState<number | null>(null)
  const [countKey, setCountKey] = useState(0)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [searchInput, setSearchInput] = useState(q)

  const updateParams = useCallback((patch: Record<string, string | null>, resetPage = true) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      Object.entries(patch).forEach(([key, value]) => {
        if (value === null || value === '') next.delete(key)
        else next.set(key, value)
      })
      if (resetPage) next.delete('page')
      return next
    }, { replace: true })
  }, [setSearchParams])

  // поиск с задержкой, чтобы не дёргать API на каждую букву
  useEffect(() => {
    if (searchInput.trim() === q) return
    const timer = window.setTimeout(() => updateParams({ q: searchInput.trim() || null }), SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [searchInput, q, updateParams])

  // URL поменялся снаружи (сброс фильтров, «Назад») — синхронизируем поле поиска
  useEffect(() => {
    setSearchInput((current) => (current.trim() === q ? current : q))
  }, [q])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    getOrders({ delivery, q, from, to, page, size: PAGE_SIZE })
      .then((data) => {
        if (cancelled) return
        setItems(data.items)
        setTotal(data.total)
      })
      .catch((err) => {
        if (cancelled) return
        console.error('[ERROR] Failed to load dealer orders', err)
        const { status, code } = apiError(err)
        if (code === 'INVALID_RANGE' || code === 'INVALID_DATE') setError('dealer.requests.error_invalid_range')
        else if (!status) setError('dealer.common.error_network')
        else setError('dealer.requests.load_failed')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [delivery, q, from, to, page, reloadKey])

  // счётчик на вкладке «Не отправлено» — с теми же поиском и периодом
  useEffect(() => {
    let cancelled = false
    getOrders({ delivery: 'NOT_SENT', q, from, to, page: 0, size: 1 })
      .then((data) => { if (!cancelled) setNotSentCount(data.total) })
      .catch((err) => console.error('[ERROR] Failed to load not-sent count', err))
    return () => { cancelled = true }
  }, [q, from, to, countKey])

  const patchItem: OrderPatch = useCallback((id, patch) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)))
    if (patch.deliveryStatus) setCountKey((k) => k + 1)
  }, [])

  const actions = useOrderActions(patchItem)
  const selected = useMemo(() => items.find((it) => it.id === selectedId) ?? null, [items, selectedId])
  const closePanel = useCallback(() => setSelectedId(null), [])

  const hasFilters = !!q || period !== 'all'
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1
  const rangeEnd = Math.min(total, (page + 1) * PAGE_SIZE)

  function handleRowKey(e: React.KeyboardEvent, id: string) {
    if (e.target !== e.currentTarget) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      setSelectedId(id)
    }
  }

  function resetFilters() {
    setSearchInput('')
    updateParams({ q: null, period: null, from: null, to: null })
  }

  return (
    <div className="dealer-requests">
      <div className="dealer-page__header">
        <h1 className="dealer-page__title">{t('dealer.requests.title')}</h1>
        <p className="dealer-page__subtitle">{t('dealer.requests.subtitle')}</p>
      </div>

      <div className="dealer-tabs" role="tablist" aria-label={t('dealer.requests.tabs_label')}>
        {TABS.map((tab) => {
          const active = tab.value === delivery
          const isNotSent = tab.value === 'NOT_SENT'
          return (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={active}
              className={`dealer-tab${active ? ' dealer-tab--active' : ''}${isNotSent ? ' dealer-tab--accent' : ''}`}
              onClick={() => updateParams({ delivery: tab.value === 'ALL' ? null : tab.value })}
            >
              {t(`dealer.requests.${tab.key}`)}
              {isNotSent && notSentCount !== null && (
                <span className={`dealer-tab__count${notSentCount > 0 ? ' dealer-tab__count--hot' : ''}`}>{notSentCount}</span>
              )}
            </button>
          )
        })}
      </div>

      <div className="dealer-toolbar">
        <label className="dealer-search">
          <SearchIcon />
          <input
            className="dealer-search__input"
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('dealer.requests.search_placeholder')}
            aria-label={t('dealer.requests.search_placeholder')}
          />
        </label>

        <div className="dealer-periods" role="group" aria-label={t('dealer.requests.period_label')}>
          {PERIODS.map((p) => (
            <button
              key={p}
              type="button"
              className={`dealer-period${period === p ? ' dealer-period--active' : ''}`}
              aria-pressed={period === p}
              onClick={() => updateParams(
                p === 'custom'
                  ? { period: 'custom', from: customFrom || lastDaysRange(7).from, to: customTo || toIsoDay(new Date()) }
                  : { period: p === 'all' ? null : p, from: null, to: null }
              )}
            >
              {t(`dealer.requests.period_${p}`)}
            </button>
          ))}
        </div>

        {period === 'custom' && (
          <div className="dealer-date-range">
            <input
              className="dealer-input dealer-input--sm"
              type="date"
              value={customFrom}
              max={customTo || undefined}
              onChange={(e) => updateParams({ from: e.target.value || null })}
              aria-label={t('dealer.requests.date_from')}
            />
            <span className="dealer-muted">—</span>
            <input
              className="dealer-input dealer-input--sm"
              type="date"
              value={customTo}
              min={customFrom || undefined}
              onChange={(e) => updateParams({ to: e.target.value || null })}
              aria-label={t('dealer.requests.date_to')}
            />
          </div>
        )}
      </div>

      {error ? (
        <div className="dealer-card dealer-empty">
          <p>{t(error)}</p>
          <button type="button" className="dealer-btn dealer-btn--secondary dealer-btn--sm" onClick={() => setReloadKey((k) => k + 1)}>
            {t('dealer.common.retry')}
          </button>
        </div>
      ) : loading && items.length === 0 ? (
        <div className="dealer-card dealer-empty"><div className="dealer-spinner dealer-requests__spinner" /></div>
      ) : items.length === 0 ? (
        <div className="dealer-card dealer-empty">
          {delivery === 'NOT_SENT' && !hasFilters ? (
            <>
              <p className="dealer-empty__title">{t('dealer.requests.empty_not_sent_title')}</p>
              <p>{t('dealer.requests.empty_not_sent_text')}</p>
            </>
          ) : (
            <>
              <p className="dealer-empty__title">{t('dealer.requests.empty_title')}</p>
              {hasFilters && (
                <button type="button" className="dealer-btn dealer-btn--secondary dealer-btn--sm" onClick={resetFilters}>
                  {t('dealer.requests.reset_filters')}
                </button>
              )}
            </>
          )}
        </div>
      ) : (
        <div className={`dealer-requests__results${loading ? ' dealer-requests__results--loading' : ''}`}>
          {/* ноутбук/десктоп: таблица */}
          <div className="dealer-card dealer-table-wrap">
            <table className="dealer-table">
              <thead>
                <tr>
                  <th>{t('dealer.orders.col_order')}</th>
                  <th>{t('dealer.orders.col_vehicle')}</th>
                  <th>{t('dealer.orders.col_customer')}</th>
                  <th>{t('dealer.orders.col_report')}</th>
                  <th>{t('dealer.orders.col_status')}</th>
                  <th><span className="dealer-visually-hidden">{t('dealer.orders.col_action')}</span></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item.id}
                    className={`dealer-table__row${isCopiedNotSent(item) ? ' dealer-table__row--attention' : ''}${selectedId === item.id ? ' dealer-table__row--selected' : ''}`}
                    onClick={() => setSelectedId(item.id)}
                    onKeyDown={(e) => handleRowKey(e, item.id)}
                    tabIndex={0}
                  >
                    <td>
                      <span className="dealer-table__primary">#{item.orderNumber}</span>
                      <span className="dealer-table__secondary" title={new Date(item.createdAt).toLocaleString(lang)}>
                        {formatDateTime(item.createdAt, lang)}
                      </span>
                      {item.mechanicName && <span className="dealer-table__secondary">{item.mechanicName}</span>}
                    </td>
                    <td>
                      <span className="dealer-table__primary">{formatVehicle(item.carYear, item.carModel) || '—'}</span>
                      {item.vin && <span className="dealer-table__secondary dealer-mono">{item.vin}</span>}
                      <span className="dealer-table__issues"><IssuePills item={item} /></span>
                    </td>
                    <td><PhoneEditor item={item} actions={actions} compact /></td>
                    <td><ReportLinkActions item={item} actions={actions} /></td>
                    <td>
                      <DeliveryBadge item={item} />
                      <CopiedHint item={item} />
                    </td>
                    <td className="dealer-table__action"><SendAction item={item} actions={actions} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* планшет/телефон: карточки */}
          <ul className="dealer-order-cards">
            {items.map((item) => (
              <li
                key={item.id}
                className={`dealer-order-card${isCopiedNotSent(item) ? ' dealer-order-card--attention' : ''}`}
                onClick={() => setSelectedId(item.id)}
                onKeyDown={(e) => handleRowKey(e, item.id)}
                tabIndex={0}
              >
                <div className="dealer-order-card__top">
                  <div>
                    <span className="dealer-table__primary">#{item.orderNumber}</span>
                    <span className="dealer-table__secondary">
                      {formatDateTime(item.createdAt, lang)}
                      {item.mechanicName ? ` · ${item.mechanicName}` : ''}
                    </span>
                  </div>
                  <DeliveryBadge item={item} withDetails={false} />
                </div>

                <div className="dealer-order-card__vehicle">
                  <span>{formatVehicle(item.carYear, item.carModel) || '—'}</span>
                  {item.vin && <span className="dealer-table__secondary dealer-mono">{item.vin}</span>}
                  <IssuePills item={item} />
                </div>

                {item.deliveryStatus === 'VIEWED' && item.lastViewedAt && (
                  <span className="dealer-table__secondary">
                    {t('dealer.orders.status_viewed_times', { count: item.viewCount })} · {t('dealer.orders.last_viewed_at', { time: formatTime(item.lastViewedAt, lang) })}
                  </span>
                )}

                <div className="dealer-order-card__actions">
                  <PhoneEditor item={item} actions={actions} compact />
                  {item.reportUrl && <ReportLinkActions item={item} actions={actions} />}
                </div>

                <CopiedHint item={item} />
                <SendAction item={item} actions={actions} block />
              </li>
            ))}
          </ul>

          <div className="dealer-pagination">
            <span className="dealer-pagination__info">
              {t('dealer.requests.pagination_info', { from: rangeStart, to: rangeEnd, total })}
            </span>
            <div className="dealer-pagination__buttons">
              <button
                type="button"
                className="dealer-icon-btn"
                disabled={page === 0}
                onClick={() => updateParams({ page: page - 1 > 0 ? String(page - 1) : null }, false)}
                aria-label={t('dealer.requests.prev_page')}
              >
                <ChevronLeftIcon />
              </button>
              <span className="dealer-pagination__page">{t('dealer.requests.page_of', { page: page + 1, pages: pageCount })}</span>
              <button
                type="button"
                className="dealer-icon-btn"
                disabled={page + 1 >= pageCount}
                onClick={() => updateParams({ page: String(page + 1) }, false)}
                aria-label={t('dealer.requests.next_page')}
              >
                <ChevronRightIcon />
              </button>
            </div>
          </div>
        </div>
      )}

      {selected && <OrderDetailPanel item={selected} actions={actions} onClose={closePanel} />}
    </div>
  )
}
